import React, { useEffect, useMemo } from "react";
import { Drawer, Button, ToggleSwitch } from 'components/common';
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { MdUpdate } from "react-icons/md";
import { FaBan, FaUserTie, FaClipboardList, FaLayerGroup, FaSitemap } from "react-icons/fa";
import AsyncSelect from 'react-select/async';
import { selectIconComponents, reactSelectCompactStyles } from "helpers/reactSelectIconControl";
import { debounce, filterOptions } from "helpers/debounce";
import { buildDepartmentOptions } from "helpers/employeeOptionHelper";
import Select from 'react-select';
import { createEmployee, getPositionGroups, updateEmployee } from "redux/organization/employeeSlice";
import { EMPLOYEE_STAFF_POLICY } from "app_config";
import MaskedInput from "react-text-mask";
import { updateUserEmployee } from "redux/authorization/userSlice";
import PropTypes from 'prop-types';
import apiAxios from "helpers/apiAxios";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';

// Stable references — inline components break react-select's menu (remount per render).
const MANAGER_SELECT_COMPONENTS = selectIconComponents(<FaUserTie size={14} className="tw-text-primary-500" />);
const STAFF_POLICY_SELECT_COMPONENTS = selectIconComponents(<FaClipboardList size={14} className="tw-text-amber-500" />);
const POSITION_GROUP_SELECT_COMPONENTS = selectIconComponents(<FaLayerGroup size={14} className="tw-text-violet-500" />);
const DEPARTMENT_SELECT_COMPONENTS = selectIconComponents(<FaSitemap size={14} className="tw-text-teal-500" />);

const UserDetailUpdateEmployeeModal = ({showModal, setShowModal, user}) => {
  const {control, register, handleSubmit, formState: {errors}, setValue, getValues } = useForm();

  const syncFullName = () => {
    const first = getValues('first_name') || '';
    const last  = getValues('last_name')  || '';
    const full  = [first, last].filter(Boolean).join(' ');
    if (full) setValue('full_name', full);
  };
  const { positionGroups } = useSelector((state) => state.organization_employee);
  const dispatch = useDispatch();
  const employee = user?.employee || user;

  const defaultManagerOption = useMemo(() => {
    if (employee?.parent?.id && employee.parent?.name) {
      return { value: employee.parent.id, label: employee.parent.name }
    }
    return null
  }, [employee?.parent])

  const defaultDepartmentOption = useMemo(() => {
    if (employee?.department?.id && employee.department?.name) {
      return { value: employee.department.id, label: employee.department.name }
    }
    return null
  }, [employee?.department])

  const staffPolicyOptions = useMemo(() => 
    EMPLOYEE_STAFF_POLICY.map((option) => ({ value: option.toLocaleLowerCase(), label: option }))
  , [])

  useEffect(() => {
    if(employee) {
      Object.entries(employee).forEach(([key, value]) => {
        switch (key) {
          case "staff_policy":
            setValue(key, value?.toLowerCase?.());
            break;
          case "start_date":
            setValue(key, value ? new Date(value) : null);
            break;
          default:
            if (value !== null && value !== undefined && typeof value !== 'object') {
              setValue(key, value);
            }
            break;
        }
      });
    }
    else if (user) {
      setValue('name', user?.name || '');
      setValue('email', user?.email || '');
      setValue('site_id', user?.site_id);
    }
  }, [setValue, employee, user])

  useEffect(() => {
    dispatch(getPositionGroups());
  }, [dispatch]);

  const handleCancel = () => {
    setShowModal(false)
  }

  const debouncedFilterManager = debounce((inputValue, callback) => {
    filterOptions("/organization/employees/public_list", inputValue).then((filteredOptions) => {
      callback(filteredOptions);
    });
  }, 300);

  const loadManagerOptions = (inputValue, callback) => {
    debouncedFilterManager(inputValue, callback);
  };

  const debouncedFilterDepartments = debounce((inputValue, callback) => {
    const siteId = employee?.site_id || user?.site_id;
    const url = siteId
      ? `/organization/sites/${siteId}/departments`
      : '/organization/departments';
    apiAxios.get(`${url}?page=1&per_page=100&name_cont=${inputValue}`)
      .then(r => callback(buildDepartmentOptions(r.data?.rows || [])))
      .catch(() => callback([]));
  }, 300);

  const loadDepartmentOptions = (inputValue, callback) => {
    debouncedFilterDepartments(inputValue, callback);
  };

  const onSubmit = async (data) => {
    console.log(data)
    try {
      let response;

      if (employee) {
        const employeeId = employee.id;
        response = await dispatch(updateEmployee({employeeId, data }))
      }
      else {
        response = await dispatch(createEmployee(data))
      }
     
      if (response && response.payload && response.payload.data && response.payload.data.attributes) {
        dispatch(updateUserEmployee(response.payload.data.attributes));
        setShowModal(false);
      } 
      else {
        toast.error("Failed to update or create employee: Invalid response structure.");
      }
    }
    catch(error) {
      console.log(error.message)
      toast.error(error.message)
    }
  }

  const positionGroupOptions = positionGroups.map((item) => ({ value: item.id, label: item.name }));

  return (
    <>
      <Drawer
        show={showModal}
        onHide={() => setShowModal(false)}
        backdropClick={false}
        width="640px"
        minWidth="480px"
        title={employee ? "Update Employee Information" : "Create Employee Information"}
        footer={
          <>
            <Button variant="primary" size="sm" onClick={handleSubmit(onSubmit)}><MdUpdate /> {employee ? "Update" : "Create"}</Button>
            <Button variant="danger" size="sm" onClick={handleCancel}><FaBan /> Cancel</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className="tw-px-2">
          <div className="tw-mb-4">
            <h6 className="tw-font-semibold tw-uppercase tw-text-blue-600 tw-mb-4" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>Basic Information</h6>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Name <span className="tw-text-red-500">*</span></label>
              <div className="tw-col-span-9">
                <input type="text" placeholder="Employee name" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("name", { required: "Name is required." })} />
                {errors.name && <span className="tw-text-red-500 tw-text-xs">{errors.name.message}</span>}
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">First name</label>
              <div className="tw-col-span-9">
                <input type="text" placeholder="First name" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("first_name", { onChange: syncFullName })} />
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Last name</label>
              <div className="tw-col-span-9">
                <input type="text" placeholder="Last name" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("last_name", { onChange: syncFullName })} />
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Full name <span className="tw-text-red-500">*</span></label>
              <div className="tw-col-span-9">
                <input type="text" placeholder="Auto-generated" readOnly className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-bg-gray-50 tw-text-gray-500" {...register("full_name", { required: "Full name is required." })} />
                {errors.full_name && <span className="tw-text-red-500 tw-text-xs">{errors.full_name.message}</span>}
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Title <span className="tw-text-red-500">*</span></label>
              <div className="tw-col-span-9">
                <input type="text" placeholder="Job title" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("title", { required: "Title is required." })} />
                {errors.title && <span className="tw-text-red-500 tw-text-xs">{errors.title.message}</span>}
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
              <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Email <span className="tw-text-red-500">*</span></label>
              <div className="tw-col-span-9">
                <input type="email" placeholder="email@example.com" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("email", { required: "Email is required." })} />
                {errors.email && <span className="tw-text-red-500 tw-text-xs">{errors.email.message}</span>}
              </div>
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Gender</label>
            <div className="tw-col-span-9">
              <Controller
                name="gender"
                control={control}
                defaultValue={employee?.gender}
                render={({ field }) => (
                  <div className="tw-flex tw-flex-row tw-gap-3">
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer">
                      <input type="radio" checked={field.value === false} onChange={() => field.onChange(false)} /> Male
                    </label>
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer">
                      <input type="radio" checked={field.value === true} onChange={() => field.onChange(true)} /> Female
                    </label>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Phone</label>
            <div className="tw-col-span-9">
              <input type="text" placeholder="Phone number" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("phone_number")} />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2 tw-mt-4">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Start date</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="start_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                    placeholderText="DD-MM-YYYY"
                    dateFormat="dd-MM-yyyy"
                    fixedHeight
                  />
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">HR Code</label>
            <div className="tw-col-span-9">
              <input type="text" placeholder="HR Code" className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500" {...register("hr_code")} />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-3">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Birthday</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="birthday"
                defaultValue={employee?.birthday}
                render={({ field }) => (
                  <MaskedInput
                    mask={[/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                    guide={false}
                    placeholder="DD-MM-YYYY"
                    name="birthday"
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Sales team</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="is_sales"
                defaultValue={true}
                render={({ field }) => (
                  <ToggleSwitch size="sm" value={!!field.value} onChange={(next) => setValue("is_sales", next)} />
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Active</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="active"
                defaultValue={true}
                render={({ field }) => (
                  <ToggleSwitch size="sm" value={!!field.value} onChange={(next) => setValue("active", next)} />
                )}
              />
            </div>
          </div>

          <hr />

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Manager</label>
            <div className="tw-col-span-9">
              <AsyncSelect
                cacheOptions
                defaultOptions
                defaultValue={defaultManagerOption}
                loadOptions={loadManagerOptions}
                closeMenuOnSelect={true}
                styles={reactSelectCompactStyles}
                components={MANAGER_SELECT_COMPONENTS}
                placeholder='Select...'
                classNamePrefix="react-select"
                className="tw-w-full"
                name="parent_id"
                onChange={(selectOptions) => setValue('parent_id', selectOptions.value)}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Staff policy</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="staff_policy"
                render={() => (
                  <Select
                    closeMenuOnSelect={true}
                    styles={reactSelectCompactStyles}
                    components={STAFF_POLICY_SELECT_COMPONENTS}
                    options={staffPolicyOptions}
                    defaultValue={staffPolicyOptions.find((option) => option.value === employee?.staff_policy?.toLocaleLowerCase())}
                    placeholder='Select...'
                    classNamePrefix="react-select"
                    onChange={selectedOption => setValue('staff_policy', selectedOption.value, {shouldValidate: true})}
                  />
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Position group</label>
            <div className="tw-col-span-9">
              <Controller
                control={control}
                name="position_group_id"
                render={({field}) => (
                  <Select
                    {...field}
                    closeMenuOnSelect={true}
                    styles={reactSelectCompactStyles}
                    components={POSITION_GROUP_SELECT_COMPONENTS}
                    options={positionGroupOptions}
                    value={positionGroupOptions.find(option => option.value === field.value) || null}
                    placeholder='Select...'
                    classNamePrefix='react-select'
                    onChange={selectedOption => setValue('position_group_id', selectedOption.value)}
                  />
                )}
              />
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-items-start tw-mb-2">
            <label className="tw-col-span-3 tw-text-sm tw-font-medium tw-text-gray-600 tw-text-right tw-pt-1.5">Department</label>
            <div className="tw-col-span-9">
              <AsyncSelect
                cacheOptions
                defaultOptions
                defaultValue={defaultDepartmentOption}
                loadOptions={loadDepartmentOptions}
                closeMenuOnSelect={true}
                styles={reactSelectCompactStyles}
                components={DEPARTMENT_SELECT_COMPONENTS}
                placeholder='Select...'
                classNamePrefix="react-select"
                className="tw-w-full"
                name="department_id"
                onChange={(selectOptions) => setValue('department_id', selectOptions.value)}
              />
            </div>
          </div>
        </form>
      </Drawer>
    </>
  );
};

UserDetailUpdateEmployeeModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

export default UserDetailUpdateEmployeeModal;